import { SubTask, Task } from "../types";

/**
 * The task-row fields the dialog's auto-save writes back.
 *
 * Shared by the two paths that persist an edited task, so they can't drift:
 * the normal save in `useTaskEditing.updateTask`, and the keepalive PATCH
 * TaskDialog fires on `pagehide` when the page is torn down mid-debounce.
 *
 * Deliberately excludes `archived` and `order_position` — the dialog never
 * edits them, and the archive button routes through its own mutation.
 */
export const TASK_UPDATE_FIELDS = [
  "title",
  "description",
  "priority",
  "list_id",
  "scheduled_date",
  "scheduled_time",
  "deadline_date",
  "duration",
  "persistent",
  "working_hours_only",
  "completed",
  "recurrence_frequency",
  "recurrence_interval",
  "recurrence_day_of_week",
  "recurrence_end_date",
] as const satisfies readonly (keyof Task)[];

export function buildTaskUpdatePayload(task: Task): Partial<Task> {
  const payload: Record<string, unknown> = {};
  for (const field of TASK_UPDATE_FIELDS) {
    payload[field] = task[field];
  }
  return payload as Partial<Task>;
}

/** The sub-task fields the save path diffs to decide what to write. */
const SUBTASK_COMPARED_FIELDS = [
  "completed",
  "title",
  "description",
  "priority",
  "scheduled_date",
  "scheduled_time",
  "deadline_date",
  "duration",
  "persistent",
  "working_hours_only",
] as const satisfies readonly (keyof SubTask)[];

/** `undefined` and `null` mean the same thing to the database. */
const norm = (value: unknown): unknown => value ?? null;

/**
 * True when two sub-task lists would produce identical writes — same members,
 * same values on every field the save path looks at. Compared by content
 * rather than reference because refetched data arrives as a fresh array on
 * every load, which would otherwise read as an edit.
 */
export function sameSubtaskContent(
  a: SubTask[] | undefined,
  b: SubTask[] | undefined,
): boolean {
  const left = a ?? [];
  const right = b ?? [];
  if (left.length !== right.length) return false;
  const rightById = new Map(right.map((s) => [s.id, s]));
  return left.every((subtask) => {
    const other = rightById.get(subtask.id);
    if (!other) return false;
    return SUBTASK_COMPARED_FIELDS.every(
      (field) => norm(subtask[field]) === norm(other[field]),
    );
  });
}

/**
 * True when two tag selections would produce the same `set_task_tags` write.
 *
 * Compared by content, and order-insensitively: a refetch rebuilds the list as
 * a fresh array in whatever order the join returned it, and neither the new
 * array identity nor a reshuffle is a user edit. Reference equality here is
 * what used to turn every background refetch into an "edit", and the save it
 * triggered refetched again — an auto-save loop that eventually wrote a stale
 * tag set back over the one the user had just picked.
 */
export function sameTagIds(
  a: readonly string[] | undefined,
  b: readonly string[] | undefined,
): boolean {
  const left = a ?? [];
  const right = b ?? [];
  if (left.length !== right.length) return false;
  const inLeft = new Set(left);
  return right.every((id) => inLeft.has(id));
}

/**
 * Structural comparison of two task snapshots, used only to answer "has the
 * user changed anything since we loaded this?". Serialising is enough here:
 * both sides are built by spreading the same Task shape, so key order matches,
 * and every field is a primitive, string or array of those. A reordered
 * subtask list serialises differently, which is correct — reordering IS an edit.
 */
export function sameTaskContent(a: Task | null, b: Task | null): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * True when `incoming` is a later save than `baseline`.
 * An equal stamp is not later. An unparseable stamp is treated as later so a
 * row we cannot order is still shown.
 */
export function isNewerRevision(incoming: string, baseline: string): boolean {
  const next = Date.parse(incoming);
  const prev = Date.parse(baseline);
  if (!Number.isFinite(next) || !Number.isFinite(prev)) return true;
  return next > prev;
}

/**
 * True when saving `next` would write something different from `prev`.
 *
 * Restricted to what the save actually persists. Everything else a `Task`
 * carries — `id`, `created_at`, the denormalized `list` / `listEmoji` display
 * fields, `tags` — is re-derived on every fetch and would otherwise make an
 * ordinary background refetch look like a user edit, costing a database write
 * and a Google Calendar sync for nothing.
 *
 * `changedFields` lists only the task-row fields; a sub-task-only change comes
 * back as `changed: true` with an empty list.
 */
export function taskUpdateDiff(
  prev: Partial<Task>,
  next: Partial<Task>,
): { changed: boolean; changedFields: (keyof Task)[] } {
  const changedFields = TASK_UPDATE_FIELDS.filter(
    (field) => norm(prev[field]) !== norm(next[field]),
  );
  const subtasksChanged = !sameSubtaskContent(prev.subtasks, next.subtasks);
  return {
    changed: changedFields.length > 0 || subtasksChanged,
    changedFields,
  };
}
